import React, { useState, useEffect } from "react";
import "./Calendar.css";

const Calendar = ({
  selectedDate,
  onDateSelect,
  disabledDays = [],
  minDate,
  maxDate,
  selectedSport,
  autoSelectFirst = true,
}) => {
  // Ensure minDate and maxDate are normalized to midnight for consistent comparisons
  const normalizedMinDate = new Date(minDate);
  normalizedMinDate.setHours(0, 0, 0, 0);
  const normalizedMaxDate = new Date(maxDate);
  normalizedMaxDate.setHours(0, 0, 0, 0);

  const effectiveDisabledDays = disabledDays;

  // Initialize the calendar display to minDate (or adjust if outside range)
  const [date, setDate] = useState(() => {
    const initialDate = new Date(normalizedMinDate);
    if (initialDate < normalizedMinDate) {
      return new Date(normalizedMinDate);
    } else if (initialDate > normalizedMaxDate) {
      return new Date(normalizedMaxDate);
    }
    return initialDate;
  });

  const getDaysInMonth = (year, month) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Shift Sunday (0) to end of the week
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const dayNames = ["M", "T", "W", "T", "F", "S", "S"]; // Start from Monday

  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth();

  const daysInCurrentMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);

  const totalCells = Math.ceil((firstDayOfMonth + daysInCurrentMonth) / 7) * 7;
  const days = [];

  // Add empty slots for the previous month's days
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }

  // Fill in current month's days
  for (let i = 1; i <= daysInCurrentMonth; i++) {
    days.push(i);
  }

  // Add empty slots for the next month's days to complete the grid
  while (days.length < totalCells) {
    days.push(null);
  }

  // Function to find the first available date within the minDate to maxDate range
  const findFirstAvailableDate = (startDate) => {
    let checkDate = new Date(startDate);

    while (checkDate <= normalizedMaxDate) {
      // Check if the date is disabled
      const isDisabled = effectiveDisabledDays.some(
        (disabledDate) =>
          disabledDate.getFullYear() === checkDate.getFullYear() &&
          disabledDate.getMonth() === checkDate.getMonth() &&
          disabledDate.getDate() === checkDate.getDate()
      );

      // Check if the date is within the allowed range
      const isBeforeMin = checkDate < normalizedMinDate;
      const isAfterMax = checkDate > normalizedMaxDate;

      if (!isDisabled && !isBeforeMin && !isAfterMax) {
        return checkDate; // Return the first non-disabled date within range
      }

      checkDate.setDate(checkDate.getDate() + 1); // Move to the next day
    }

    // If no available date is found, return minDate
    return new Date(normalizedMinDate);
  };

  // Initialize with the first available date within range if no date is selected.
  // Callers that want the user to pick explicitly (e.g. the activities page) pass
  // autoSelectFirst={false} to suppress this.
  useEffect(() => {
    if (autoSelectFirst && !selectedDate) {
      const defaultDate = findFirstAvailableDate(normalizedMinDate);
      onDateSelect(defaultDate);
    }
  }, [selectedSport]); // Add selectedSport to dependencies to re-run when it changes

  // Determine if the "Previous Month" button should be disabled
  const isPrevMonthDisabled = () => {
    const prevMonthDate = new Date(currentYear, currentMonth - 1, 1);
    const minMonthStart = new Date(
      normalizedMinDate.getFullYear(),
      normalizedMinDate.getMonth(),
      1
    );
    return prevMonthDate < minMonthStart; // Disable if previous month is before minDate's month
  };

  // Determine if the "Next Month" button should be disabled
  const isNextMonthDisabled = () => {
    const nextMonthDate = new Date(currentYear, currentMonth + 1, 1);
    const maxMonthStart = new Date(
      normalizedMaxDate.getFullYear(),
      normalizedMaxDate.getMonth(),
      1
    );
    return nextMonthDate > maxMonthStart; // Disable if next month is after maxDate's month
  };

  const handlePrevMonth = () => {
    if (!isPrevMonthDisabled()) {
      setDate(new Date(currentYear, currentMonth - 1, 1));
    }
  };

  const handleNextMonth = () => {
    if (!isNextMonthDisabled()) {
      setDate(new Date(currentYear, currentMonth + 1, 1));
    }
  };

  const handleDayClick = (day) => {
    if (day === null) return; // Ignore clicks on empty squares

    const currentDate = new Date(currentYear, currentMonth, day);
    // Check if the current date matches any disabled date (year, month, day)
    const isDisabled = effectiveDisabledDays.some(
      (disabledDate) =>
        disabledDate.getFullYear() === currentDate.getFullYear() &&
        disabledDate.getMonth() === currentDate.getMonth() &&
        disabledDate.getDate() === currentDate.getDate()
    );

    // Check if the date is before minDate or after maxDate
    const isBeforeMin = currentDate < normalizedMinDate;
    const isAfterMax = currentDate > normalizedMaxDate;

    if (isDisabled || isBeforeMin || isAfterMax) return; // Ignore clicks on disabled, past, or future dates

    onDateSelect(currentDate); // Pass the full Date object
  };

  const handleYearChange = (event) => {
    const newYear = parseInt(event.target.value);
    // Ensure the new year/month combination is within the minDate to maxDate range
    const newDate = new Date(newYear, currentMonth, 1);
    const minMonthStart = new Date(
      normalizedMinDate.getFullYear(),
      normalizedMinDate.getMonth(),
      1
    );
    const maxMonthStart = new Date(
      normalizedMaxDate.getFullYear(),
      normalizedMaxDate.getMonth(),
      1
    );

    if (newDate < minMonthStart) {
      setDate(
        new Date(
          normalizedMinDate.getFullYear(),
          normalizedMinDate.getMonth(),
          1
        )
      );
    } else if (newDate > maxMonthStart) {
      setDate(
        new Date(
          normalizedMaxDate.getFullYear(),
          normalizedMaxDate.getMonth(),
          1
        )
      );
    } else {
      setDate(newDate);
    }
  };

  const handleMonthChange = (event) => {
    const newMonth = parseInt(event.target.value);
    // Ensure the new month/year combination is within the minDate to maxDate range
    const newDate = new Date(currentYear, newMonth, 1);
    const minMonthStart = new Date(
      normalizedMinDate.getFullYear(),
      normalizedMinDate.getMonth(),
      1
    );
    const maxMonthStart = new Date(
      normalizedMaxDate.getFullYear(),
      normalizedMaxDate.getMonth(),
      1
    );

    if (newDate < minMonthStart) {
      setDate(
        new Date(
          normalizedMinDate.getFullYear(),
          normalizedMinDate.getMonth(),
          1
        )
      );
    } else if (newDate > maxMonthStart) {
      setDate(
        new Date(
          normalizedMaxDate.getFullYear(),
          normalizedMaxDate.getMonth(),
          1
        )
      );
    } else {
      setDate(newDate);
    }
  };

  // Filter years to include only those between minDate and maxDate
  const years = Array.from(
    {
      length:
        normalizedMaxDate.getFullYear() - normalizedMinDate.getFullYear() + 1,
    },
    (_, i) => normalizedMinDate.getFullYear() + i
  );

  // Filter months based on the current year
  const availableMonths = monthNames.filter((_, index) => {
    const monthDate = new Date(currentYear, index, 1);
    const minMonthStart = new Date(
      normalizedMinDate.getFullYear(),
      normalizedMinDate.getMonth(),
      1
    );
    const maxMonthStart = new Date(
      normalizedMaxDate.getFullYear(),
      normalizedMaxDate.getMonth(),
      1
    );
    return monthDate >= minMonthStart && monthDate <= maxMonthStart;
  });

  return (
    <div className="calendar">
      <div className="calendar-header">
        <button
          onClick={handlePrevMonth}
          className="calendar-nav-button"
          disabled={isPrevMonthDisabled()}
        >
          &lt;
        </button>
        <div className="month-year-select">
          <select value={currentMonth} onChange={handleMonthChange}>
            {availableMonths.map((month, index) => {
              const monthIndex = monthNames.indexOf(month);
              return (
                <option value={monthIndex} key={month}>
                  {month}
                </option>
              );
            })}
          </select>
          <select value={currentYear} onChange={handleYearChange}>
            {years.map((year) => (
              <option value={year} key={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={handleNextMonth}
          className="calendar-nav-button"
          disabled={isNextMonthDisabled()}
        >
          &gt;
        </button>
      </div>
      <div className="days-grid">
        {dayNames.map((dayName) => (
          <div key={dayName} className="day day-of-week">
            {dayName}
          </div>
        ))}
        {days.map((day, index) => {
          const currentDate = day !== null ? new Date(currentYear, currentMonth, day) : null;
          const isSelected =
            selectedDate &&
            day !== null &&
            selectedDate.getDate() === day &&
            selectedDate.getMonth() === currentMonth &&
            selectedDate.getFullYear() === currentYear;

          // Check if the current day is disabled by comparing year, month, and day
          const isDisabled =
            day !== null &&
            effectiveDisabledDays.some(
              (disabledDate) =>
                disabledDate.getFullYear() === currentDate.getFullYear() &&
                disabledDate.getMonth() === currentDate.getMonth() &&
                disabledDate.getDate() === currentDate.getDate()
            );

          // Check if the date is before minDate or after maxDate
          const isBeforeMin = day !== null && currentDate < normalizedMinDate;
          const isAfterMax = day !== null && currentDate > normalizedMaxDate;

          return (
            <div
              key={index}
              className={`day ${day === null ? "empty" : ""} ${
                isSelected ? "selected" : ""
              } ${isDisabled || isBeforeMin || isAfterMax ? "disabled" : ""}`}
              onClick={() => handleDayClick(day)}
            >
              {day !== null ? day : ""}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;